/**
 * Offscreen Fabric canvases for rendering stored pages without touching the
 * live editor. Shared by page export, page thumbnails and (later) the video
 * frame compositor, which previously each carried their own copy.
 */

import fabric from '@/lib/fabric';
import type { FabricCanvasData } from '@canvas/shared-types';
import { registerVideoObject } from '@/lib/video/fabricVideoObject';

/**
 * Create a StaticCanvas sized to the page and load its objects into it.
 * The caller owns the result and must `dispose()` it.
 */
export async function createSceneStaticCanvas(
  page: FabricCanvasData,
  options: { transparentBackground?: boolean } = {}
): Promise<fabric.Canvas> {
  const el = document.createElement('canvas');
  // Fabric v5 is CJS; StaticCanvas is not always exposed through the interop shim.
  const StaticCanvas =
    (fabric as unknown as { StaticCanvas?: typeof fabric.Canvas }).StaticCanvas ||
    fabric.Canvas;
  const canvas = new StaticCanvas(el, {
    width: page.width || 1200,
    height: page.height || 800,
    renderOnAddRemove: false,
    enableRetinaScaling: false,
  });

  const background = options.transparentBackground
    ? ''
    : page.background || '#ffffff';

  // Before loadFromJSON, or Fabric silently drops video layers (see fabricVideoObject.ts).
  registerVideoObject();
  try {
    await new Promise<void>((resolve, reject) => {
      canvas.loadFromJSON(
        {
          version: page.version || '5.3.0',
          objects: page.objects || [],
          background,
        },
        () => {
          try {
            canvas.renderAll();
            resolve();
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  } catch (err) {
    canvas.dispose();
    throw err;
  }

  return canvas;
}

/** Render a page offscreen, run `fn` against it, and always dispose. */
export async function withOffscreenCanvas<T>(
  page: FabricCanvasData,
  transparentBackground: boolean,
  fn: (canvas: fabric.Canvas) => T | Promise<T>
): Promise<T> {
  const canvas = await createSceneStaticCanvas(page, { transparentBackground });
  try {
    return await fn(canvas);
  } finally {
    canvas.dispose();
  }
}
